import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { User, KeyRound, Mail, Shield, ShieldCheck, Save } from "lucide-react";

const inputCls =
  "w-full rounded-xl border border-[#e2e8f0] bg-white px-3.5 py-2.5 text-sm text-[#1a202c] focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/15 outline-none transition-all";
const labelCls = "text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold";

export default function Account() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change password");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  const isAdmin = user.role === "admin";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="account-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Account</div>
        <h1 className="font-display text-3xl mt-0.5 text-[#1a202c]">Your account</h1>
        <p className="text-xs text-[#718096] mt-1">Manage your profile details and change your password.</p>
      </div>

      {/* Profile summary */}
      <div className="card-soft p-5 flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-brand text-white flex items-center justify-center text-base font-bold shadow-[0_4px_12px_-2px_rgba(56,178,172,0.4)]">
          {(user.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl text-[#1a202c]" data-testid="account-name">{user.name}</div>
          <div className="text-xs text-[#4a5568] mt-0.5 flex items-center gap-1.5">
            <Mail className="h-3 w-3 text-[#a0aec0]" /> {user.email}
          </div>
          <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-1">
            Member since {new Date(user.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] uppercase tracking-widest font-bold border ${
            isAdmin
              ? "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]"
              : "bg-[#e0f2fe] text-[#075985] border-[#7dd3fc]"
          }`}
          data-testid="account-role-badge"
        >
          {isAdmin ? <ShieldCheck className="h-3 w-3" strokeWidth={2.6} /> : <Shield className="h-3 w-3" strokeWidth={2.4} />}
          {user.role}
        </span>
      </div>

      {/* Change password */}
      <div className="card-soft p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="h-7 w-7 rounded-lg bg-[#ecfeff] border border-[#67e8f9] flex items-center justify-center">
            <KeyRound className="h-3.5 w-3.5 text-[#0e7490]" strokeWidth={2.4} />
          </div>
          <h2 className="font-display text-base text-[#1a202c]">Change password</h2>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Current password</label>
            <input
              data-testid="account-current-pw"
              type="password"
              required
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className={`${inputCls} mt-1.5 font-mono-tight`}
              autoComplete="current-password"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>New password</label>
              <input
                data-testid="account-new-pw"
                type="password"
                required
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className={`${inputCls} mt-1.5 font-mono-tight`}
                autoComplete="new-password"
              />
              <div className="mt-1 text-[10px] text-[#a0aec0]">At least 6 characters.</div>
            </div>
            <div>
              <label className={labelCls}>Confirm new password</label>
              <input
                data-testid="account-confirm-pw"
                type="password"
                required
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className={`${inputCls} mt-1.5 font-mono-tight`}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              data-testid="account-save-pw"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
